import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { clientsApi } from '@/lib/api'
import { Lightbulb, FileText, BookOpen, Loader2, MessageSquare, Hash } from 'lucide-react'
import { cn } from '@/lib/utils'

interface RAGHintPopupProps {
  clientId: string
  sourceText: string
  isVisible: boolean
  position: { x: number; y: number }
}

interface HintItem {
  type: 'rag' | 'feedback' | 'term'
  title: string
  content: string
  subtitle?: string
}

/**
 * RAG提示浮窗组件
 * 在用户悬停段落时显示客户历史反馈、RAG内容和匹配术语
 */
export function RAGHintPopup({ clientId, sourceText, isVisible, position }: RAGHintPopupProps) {
  const [showPopup, setShowPopup] = useState(false)
  const popupRef = useRef<HTMLDivElement | null>(null)
  const [placement, setPlacement] = useState<{ left: number; top: number }>({ left: 0, top: 0 })

  const { data: hintsData, isLoading } = useQuery({
    queryKey: ['rag-hints', clientId, sourceText],
    queryFn: () => clientsApi.getRAGHints(clientId, sourceText),
    enabled: isVisible && !!clientId && sourceText.length > 10,
    staleTime: 30000,
  })

  const hints = (() => {
    if (!hintsData?.data) return []

    const result: HintItem[] = []
    const data = hintsData.data

    if (data.rag && data.rag.length > 0) {
      result.push({
        type: 'rag',
        title: '客户要求',
        content: data.rag.join('\n'),
      })
    }

    if (data.feedbacks && data.feedbacks.length > 0) {
      data.feedbacks.forEach(fb => {
        result.push({
          type: 'feedback',
          title: '历史反馈',
          subtitle: fb.fileName,
          content: fb.rawContent || '',
        })
      })
    }

    if (data.terms && data.terms.length > 0) {
      data.terms.forEach(term => {
        result.push({
          type: 'term',
          title: '匹配术语',
          content: `${term.source} → ${term.target}${term.context ? ` (${term.context})` : ''}`,
        })
      })
    }

    return result
  })()

  useEffect(() => {
    if (isVisible) {
      const timer = setTimeout(() => setShowPopup(true), 500)
      return () => clearTimeout(timer)
    } else {
      setShowPopup(false)
    }
  }, [isVisible])

  useEffect(() => {
    if (!showPopup) return
    const el = popupRef.current
    if (!el) return

    const margin = 12
    const offset = 12
    const rect = el.getBoundingClientRect()

    let left = position.x + offset
    if (left + rect.width + margin > window.innerWidth) {
      left = position.x - rect.width - offset
    }
    left = Math.max(margin, Math.min(left, window.innerWidth - rect.width - margin))

    let top = position.y + offset
    if (top + rect.height + margin > window.innerHeight) {
      top = position.y - rect.height - offset
    }
    top = Math.max(margin, Math.min(top, window.innerHeight - rect.height - margin))

    setPlacement({ left, top })
  }, [showPopup, position.x, position.y, hints.length, isLoading])

  if (!showPopup || hints.length === 0) {
    return null
  }

  const getIcon = (type: HintItem['type']) => {
    switch (type) {
      case 'rag':
        return <FileText className="h-3 w-3 text-info" />
      case 'feedback':
        return <MessageSquare className="h-3 w-3 text-warning" />
      case 'term':
        return <Hash className="h-3 w-3 text-success" />
      default:
        return <BookOpen className="h-3 w-3 text-muted-foreground" />
    }
  }

  return (
    <div
      ref={popupRef}
      className={cn(
        "fixed z-50 w-80 bg-popover border rounded-lg shadow-lg p-3 animate-in fade-in-0 zoom-in-95",
        "data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95"
      )}
      style={{
        left: placement.left,
        top: placement.top,
      }}
    >
      <div className="flex items-center gap-2 mb-2 pb-2 border-b">
        <Lightbulb className="h-4 w-4 text-warning" />
        <span className="text-xs font-medium">智能提示</span>
        {isLoading && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
        <span className="text-[10px] text-muted-foreground ml-auto">
          {hints.length} 条相关
        </span>
      </div>

      <div className="space-y-2 max-h-56 overflow-y-auto">
        {hints.map((hint, index) => (
          <div key={index} className="text-xs p-2 rounded bg-muted/30">
            <div className="flex items-center gap-1.5 mb-1">
              {getIcon(hint.type)}
              <span className="font-medium text-muted-foreground">{hint.title}</span>
              {hint.subtitle && (
                <span className="text-[10px] text-muted-foreground/70 ml-auto truncate max-w-[100px]">
                  {hint.subtitle}
                </span>
              )}
            </div>
            <p className="text-muted-foreground leading-relaxed pl-4 whitespace-pre-wrap">
              {hint.content.length > 150 ? hint.content.substring(0, 150) + '...' : hint.content}
            </p>
          </div>
        ))}
      </div>

      <div className="mt-2 pt-2 border-t text-[10px] text-muted-foreground/60 text-center">
        悬停 0.5 秒后显示 · 基于 RAG 和历史反馈
      </div>
    </div>
  )
}
